import { z } from 'zod/v4';

import type { Custody } from '@infinex/types';
import { WALLET_ICONS } from '@infinex/ui-assets/wallet-icons';
import { decorativeColors } from '@infinex/ui-constants';
import type { AvatarColorKey, AvatarIconKey } from '@infinex/ui/components';

import type { CustodyIconType } from '../../../custody';
import type { AvatarIconOrEmojiValue } from '../types';

export function buildAvatarFromWallet({
  avatarIcon,
  avatarColor,
  avatarEmoji,
}: {
  avatarIcon: string | null;
  avatarColor: string | null;
  avatarEmoji: string | null;
}): AvatarIconOrEmojiValue | undefined {
  if (avatarEmoji) {
    if (!isValidEmoji(avatarEmoji)) {
      return undefined;
    }

    return {
      type: 'emoji',
      emoji: avatarEmoji,
    };
  }

  if (avatarIcon && avatarColor) {
    if (!isValidIcon(avatarIcon, avatarColor)) {
      return undefined;
    }

    return {
      type: 'icon',
      icon: avatarIcon as AvatarIconKey,
      color: avatarColor as AvatarColorKey,
    };
  }

  return undefined;
}

export function getWalletAvatarIconWithCustody(
  custody: Custody | CustodyIconType
): AvatarIconKey {
  const custodyIconMap: Record<Custody | CustodyIconType, AvatarIconKey> = {
    wallet: 'infinex',
    infinex: 'infinex',
    imported: 'wallet',
    vault: 'shield', // this won't happen but just let typescript happy
    'external-signer': 'trezor',
    // Safe is the only multi-sig protocol today — use its brand mark as the
    // member-wallet default (groups use `keys` instead).
    'multi-sig': 'safe',
    ledger: 'ledger',
    trezor: 'trezor',
  };
  return custodyIconMap[custody] ?? 'wallet';
}

export function getWalletAvatarColorWithCustody(
  custody: Custody | CustodyIconType
): AvatarColorKey {
  return custody === 'multi-sig' ? 'green' : 'orange';
}

function isValidEmoji(emoji: string) {
  return z.emoji().safeParse(emoji).success;
}

function isValidIcon(icon: string, color: string) {
  return icon in WALLET_ICONS && color in decorativeColors;
}
